import { ClaimRow } from 'api/endpoints';
import Excel from 'exceljs';
import _ from 'lodash';
import compact from 'lodash/compact';
import times from 'lodash/times';
import {
  FootnoteAppear,
  StructureFooter,
  StructureHeader,
} from 'pages/ExhibitExporterPage/typesExhibitExporter';
import { formatMoney } from 'utils/formatNumber';
import { iif } from 'utils/object';
import { ExhibitTemplateProps } from '../exhibitType';
import { ClaimsWithSurviving, IHeaderFooter } from './type';

export const pageNumberValue = 'Page &P';
const REDACTED_MSG = 'REDACTED';

const excelTemplatePath = `${process.env.PUBLIC_URL}/templates`;

// @todo: move to global utils file
export const loadTemplate = async (
  templateName: string,
  templateType: string = 'exhibit-exporter',
) => {
  const response = await fetch(
    `${excelTemplatePath}/${templateType}/${templateName}`,
  );
  return await response.arrayBuffer();
};

export const insertNumberOrEmptyRows = (
  sheet: Excel.Worksheet,
  position: number,
  count: number,
) => {
  sheet.insertRows(
    position,
    times(count, () => []),
  );
};

export const getSurvivingTotalCurrency = (
  claims: ClaimsWithSurviving[],
  fieldName: keyof ClaimRow,
): string => {
  const result = _(claims)
    .map((r) => getTotalAmount(r.survivingClaims, fieldName))
    .sum();

  return formatMoney(result);
};

export const getTotalCurrency = (
  claims: ClaimRow[],
  fieldName: keyof ClaimRow,
): string => {
  const value = getTotalAmount(claims, fieldName);

  return formatMoney(value);
};

export const getAddressLine = (
  c: ClaimRow | undefined,
  redactNameAddress?: boolean,
) => {
  if (!c) return '';
  if (redactNameAddress) return REDACTED_MSG;

  const addresses = [c.address1, c.address2, c.address3, c.address4]
    .map((a) => a?.trim())
    .filter((address) => address && address.length)
    .join('\n');

  return `${c.counterpartyName ?? ''} ${showValueWithNewLine(
    addresses,
  )} ${showValueWithNewLine(
    [c.city, c.state, c.zip]
      .map((a) => a?.trim())
      .filter((c) => c && c.length)
      .join(' '),
  )} ${showValueWithNewLine(c.country)}`;
};

function getTotalAmount(claims: ClaimRow[], fieldName: keyof ClaimRow) {
  return _(claims)
    .map((r) => r[fieldName])
    .sum();
}

export function createClaimAmount(c: ClaimRow | undefined): string {
  if (!c) return '';

  return (
    `${formatMoney(c.currentSecured)} (S)\n` +
    `${formatMoney(c.currentAdministrative)} (A)\n` +
    `${formatMoney(c.currentPriority)} (P)\n` +
    `${formatMoney(c.currentUnsecured)} (U)\n` +
    `${formatMoney(c.currentTotal)} (T)`
  );
}

export function createReducedClaimAmount(c: ClaimRow): string {
  return (
    `${formatMoney(c.proposedSecured)} (S)\n` +
    `${formatMoney(c.proposedAdministrative)} (A)\n` +
    `${formatMoney(c.proposedPriority)} (P)\n` +
    `${formatMoney(c.proposedUnsecured)} (U)\n` +
    `${formatMoney(c.proposedTotal)} (T)`
  );
}

export const createHeaderFooter = (props: ExhibitTemplateProps): IHeaderFooter => {
  const { exhibit, primaryCaseNumber, projectName, actionName } = props;
  const { footer, header } = exhibit.structure;

  const showGlobalFooter = footer.includes(StructureFooter.Footnote);

  const showHearingName = header.includes(StructureHeader.Hearing);
  const showExhibitName = header.includes(StructureHeader.Exhibit);
  const showBasisForObjct = header.includes(StructureHeader.Objection);
  const showCaseNumber = header.includes(StructureHeader.CaseNumber);
  const showCaseName = header.includes(StructureHeader.CaseName);

  const actionNameText = iif(showHearingName, actionName);
  const exhibitName = iif(showExhibitName, exhibit.exhibitNumber ?? '');
  const basisForObj = iif(showBasisForObjct, exhibit.objectionBasis);
  const caseNumber = iif(showCaseNumber, primaryCaseNumber);
  const caseName = iif(showCaseName, projectName);

  const fistPageGlobal: string[] = showGlobalFooter
    ? exhibit.footer.map((r) => r.text)
    : [];

  const allPageGlobal: string[] = showGlobalFooter
    ? exhibit.footer
        .filter((r) => r.optionId !== FootnoteAppear.First)
        .map((r) => r.text)
    : [];

  return {
    header: {
      left: multilineText(actionNameText, basisForObj),
      middle: multilineText(exhibitName),
      right: multilineText(
        showValueWithLabel('Primary case number', caseNumber),
        showValueWithLabel('Project name', caseName),
      ),
    },
    bottomFirst: {
      left: multilineFooterText(...fistPageGlobal),
      middle: '',
      right: pageNumberValue,
    },
    bottom: {
      left: multilineFooterText(...allPageGlobal),
      middle: '',
      right: pageNumberValue,
    },
  };
};

const multilineFooterText = (...value: (string | null | undefined)[]) => {
  const text = multilineText(...value);
  // character "&" is a special character that is used for formatting
  const withAllowedCharacters = text.replaceAll('&', '&&');
  return withAllowedCharacters;
};

const multilineText = (...value: (string | null | undefined)[]) =>
  compact(value).join(`\n`);

const showValueWithNewLine = (value: string | undefined) =>
  value ? `\n${value}` : '';

const showValueWithLabel = (label: string, value?: string | number) => {
  return value ? `${label}: ${value}` : '';
};

export const getChildDataByRefNumber = (
  childClaims: ClaimRow[],
  referenceNumber: string,
) => {
  const originalDebtors: string[] = [];
  const proposedDebtors: string[] = [];
  const claimAmounts: string[] = [];
  const reducedClaimAmounts: string[] = [];

  childClaims
    .filter((c) => c.parentClaimNumber === referenceNumber)
    .forEach((c) => {
      if (c.originalDebtor) {
        originalDebtors.push(c.originalDebtor);
      }
      if (c.proposedDebtor) {
        proposedDebtors.push(c.proposedDebtor);
      }
      claimAmounts.push(createClaimAmount(c));
      reducedClaimAmounts.push(createReducedClaimAmount(c));
    });
  return {
    originalDebtors: formatListToString(originalDebtors, '\n\n\n\n\n\n'), // 6 new lines bc of 5 claimAmounts lines and blank line
    proposedDebtors: formatListToString(proposedDebtors, '\n\n\n\n\n\n'),
    claimAmounts: formatListToString(claimAmounts, '\n\n'),
    reducedClaimAmounts: formatListToString(reducedClaimAmounts, '\n\n'),
  };
};

const formatListToString = (toJoin: string[], joinWith: string) => {
  if (!toJoin.length) return;
  return toJoin.join(joinWith);
};
